import type * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { createFilterOptions } from '@mui/material';
import type {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteValue,
} from '@mui/base/useAutocomplete/useAutocomplete';
import { intersection } from 'lodash-es';
import { useModal } from 'hooks';

export type OptionValue = number | string | number[];

export interface Option {
  value: OptionValue;
  label: string;
}

export const SELECT_ALL_VALUE = 'select-all';

const SELECT_ALL_OPTION: Option = { label: 'Select all', value: SELECT_ALL_VALUE };

const filter = createFilterOptions<Option>();

interface UseCheckboxAutocompleteParams {
  options: Option[];
  value?: OptionValue[];
  allSelectable: boolean;
  onChange: (value: OptionValue[]) => void;
}

const useCheckboxAutocomplete = ({ options, value, allSelectable, onChange }: UseCheckboxAutocompleteParams) => {
  const { isOpen: isFocused, open: onFocus, close } = useModal(false);
  const [inputValue, setInputValue] = useState('');

  const selectedOptions = useMemo(() => options.filter((o) => value?.includes(o.value)), [value, options]);
  const matchedOptions = useMemo(
    () => filter(options, { inputValue, getOptionLabel: (o) => o.label }),
    [options, inputValue]
  );
  const allSelected = !!matchedOptions.length && matchedOptions.every((o) => value?.includes(o.value));

  // Already selected options stay visible while the list is filtered by text.
  const listOptions = useMemo(() => {
    const selectedOutsideFilter = selectedOptions.filter((o) => !matchedOptions.includes(o));
    const visible = [...selectedOutsideFilter, ...matchedOptions];

    return allSelectable ? [SELECT_ALL_OPTION, ...visible] : visible;
  }, [allSelectable, selectedOptions, matchedOptions]);

  // "Select all" applies to the text matches only, so a filtered list can be added to the selection.
  const toggleSelectAll = useCallback(() => {
    const matchedValues = matchedOptions.map((o) => o.value);
    if (allSelected) {
      onChange((value ?? []).filter((v) => !matchedValues.includes(v)));
    } else {
      onChange([...(value ?? []), ...matchedValues.filter((v) => !value?.includes(v))]);
    }
  }, [allSelected, matchedOptions, onChange, value]);

  const isOptionChecked = useCallback(
    (option: Option, selected: boolean) => {
      if (option.value === SELECT_ALL_VALUE) {
        return allSelected;
      }

      if (Array.isArray(option.value)) {
        return !!option.value.length && intersection(value, option.value).length === option.value.length;
      }

      return selected;
    },
    [allSelected, value]
  );

  const handleChange = useCallback(
    (
      event: React.SyntheticEvent,
      newValue: AutocompleteValue<Option, true, false, false>,
      reason: AutocompleteChangeReason
    ) => {
      if (reason === 'selectOption' || reason === 'removeOption') {
        if (newValue.some((option) => option.value === SELECT_ALL_VALUE)) {
          toggleSelectAll();
        } else {
          onChange(newValue.map((o) => o.value));
        }
      } else if (reason === 'clear') {
        onChange([]);
      }
    },
    [onChange, toggleSelectAll]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, newInputValue: string, reason: AutocompleteInputChangeReason) => {
      // MUI resets the input on every selection in multiple mode, which would drop the search text
      if (reason !== 'reset') {
        setInputValue(newInputValue);
      }
    },
    []
  );

  const onBlur = useCallback(() => {
    close();
    setInputValue('');
  }, [close]);

  return {
    isFocused,
    inputValue,
    selectedOptions,
    listOptions,
    isOptionChecked,
    onFocus,
    onBlur,
    handleChange,
    handleInputChange,
  };
};

export default useCheckboxAutocomplete;
